interface Prioritizable<TId> {
  id: TId;
  priority: number;
}

/**
 * after the user requests to move an objective in a priority list, computes
 * the set of objectives which must be reprioritized to ensure that the sorted
 * list is in the requested order, and what their new priorities are.
 *
 * @param objectives the original list of objectives, which is assumed to be
 *   in sorted order
 * @param moveFromIndex the index of the element being moved
 * @param moveToIndex the index to which the element is moving
 * @returns an object containing:
 *   * after: a new list of objectives in the requested order, sorted by
 *       priority and then id;
 *   * modifiedPriorities: a map of objective ids whose priorities were
 *       modified, and the new priority assigned to them.
 */
export function reprioritize<T extends Prioritizable<any>>(
  objectives: T[],
  moveFromIndex: number,
  moveToIndex: number
): {
  after: T[];
  modifiedPriorities: Map<T["id"], number>;
} {
  if (
    moveFromIndex < 0 ||
    moveFromIndex >= objectives.length ||
    moveToIndex < 0 ||
    moveToIndex > objectives.length
  ) {
    throw new Error(
      `reprioritize: error: Invalid moveFromIndex or moveToIndex`
    );
  }

  // the original implementation of this method was designed to be as
  // conservative as possible about which objectives it would change the
  // priorities of, and the numeric values of the integer priorities it would
  // produce; there's a hard limit to the representable range of integers above
  // at Number.MAX_SAFE_INTEGER[1], and every reprioritization we perform costs
  // a few bytes on the wire.
  //
  // however, trying to be too clever here gets you into the heartache of
  // trying to keep the natural order (the order ignoring priority) of sequence
  // items synced between frontend and backend code, which creates deeply
  // mysterious bugs and misbehaviors around reprioritization.  (cf. bug
  // SOFTWARE-5826)
  //
  // this reimplementation uses up the number line like it's going out of style,
  // assumes the input list comes pre-sorted, and always crushes the priorities
  // of the items in the list to a canonical value and writes all of them back,
  // removing the need for keeping the two pieces of code in sync.
  //
  // [1]: sidebar at the end of the file.
  const after: T[] = [];
  const modifiedPriorities = new Map<T["id"], number>();

  const movingObjective = objectives[moveFromIndex];
  if (!movingObjective) {
    return { after, modifiedPriorities };
  }

  // this method places a single objective at the back of the output array,
  // modifying its priority.
  function placeObjective(obj: T): void {
    const newPriority = after.length + 1;

    modifiedPriorities.set(obj.id, newPriority);
    after.push({
      ...obj,
      priority: newPriority,
    });
  }

  let fromIndex = 0;

  while (after.length < objectives.length) {
    if (fromIndex === moveFromIndex) {
      // this element is moving somewhere else; skip to the next one.
      fromIndex++;
      continue;
    }

    if (after.length === moveToIndex) {
      // the output array is full enough that we need to find the moving
      // objective a landing spot.
      placeObjective(movingObjective);
      continue;
    }

    const obj = objectives[fromIndex];
    if (!obj) {
      // we shouldn't run off the end of the array but if we do somehow,
      // terminate to prevent an infinite loop.
      console.error(
        `failed to reprioritize ${JSON.stringify(
          objectives
        )}? (${moveFromIndex} -> ${moveToIndex})`
      );
      break;
    }

    placeObjective(obj);
    fromIndex++;
  }

  return { after, modifiedPriorities };
}

// SIDEBAR: an alternative to this whole system we considered was to have
// fractional priorities ranging between e.g. 0 and 1, then upon reordering a
// list A-B-C to A-C-B, setting C's priority to (A + B) / 2.  this prevents the
// priorities from growing unbounded, and also allows us to only reprioritize
// one item per operation. this sounds good at first, but floating-point numbers
// aren't the reals, just a 53-bit fixed-point number (the significand) scaled
// by a power of 2, and e.g. if A has priority of 0.5 (represented as (1.)0 x
// 2^-1, using 0 bits of significand) and B has priority of 0.625 (represented
// as (1.)01 x 2^-1, using 2 bits of significand), C will get a priority of
// 0.5625 -- represented as (1.)001 x 2^-1, using 3 bits of significand.  in the
// worst case, we burn 1 bit of precision per reprioritization, and start
// having rounding errors after just over 50 operations.
